import { isStaffTest } from './staff-test'

/**
 * Fundraising funnel events, sent from code straight to the Matomo tracker (`_paq`) so they depend
 * on neither the form's markup nor Tag Manager config. Category = form type, action = funnel step,
 * name = form id, value = amount, and the campaign code and organisation as custom dimensions. The
 * event list is in apps/docs (act-platform/matomo-events).
 *
 * Only ids, codes and an amount ever go out: every field is checked against a strict shape and
 * dropped if it doesn't fit, so a donor's name, email or personnummer can't reach Matomo even by
 * mistake.
 */

/**
 * Form types: the token the form ids start with (`<type>-<lang>-<slug>-xxxx`), and the Matomo event
 * category. `giftmembership`, `giftdonation` and `memorial` are provisional until the forms CMS
 * settles its names.
 */
export const FORM_TYPES = [
  'donation',
  'monthly',
  'membership',
  'giftmembership',
  'giftdonation',
  'memorial',
  'petition',
  // Not forms CMS types: the quiz's in-card raise and Blixt (digital volunteer) join.
  'raise',
  'flashvol',
] as const
export type FormType = (typeof FORM_TYPES)[number]

/** Who pays: a dimension of a form type, not a type of its own. */
export const FORM_PAYERS = ['private', 'company'] as const
export type FormPayer = (typeof FORM_PAYERS)[number]

/** The organisation a form collects for, as act's SITE_ORG names it. */
export const FORM_ORGS = ['djurensratt', '1882'] as const
export type FormOrg = (typeof FORM_ORGS)[number]

export type TrackAction = 'start' | `step:${string}` | 'submit' | 'accepted' | 'error'

export interface TrackEvent {
  /** Matomo event category. */
  type: FormType
  payer?: FormPayer
  action: TrackAction
  /** The form's id or slug, set by the host. Matomo event name. */
  name?: string
  /** The amount, in whole currency units. Matomo event value. */
  value?: number
  /** The CRM campaign code the form submits with (gift structure or recruitment code). */
  campaign?: string
  org?: FormOrg
  /** The language the form renders in, e.g. `sv`. Reported with the org as `<org>-<lang>`. */
  lang?: string
}

/** An event as Matomo receives it, after the allow-list. */
export interface MatomoEvent {
  category: FormType
  action: string
  name?: string
  value?: number
  campaign?: string
  /** `djurensratt-sv`, `1882-en`…; the org alone when the language is unknown. */
  orgLang?: string
}

/** The action-scope custom dimensions every site in MATOMO_SITES has under these ids. */
export const MATOMO_DIMENSIONS = { campaign: 3, orgLang: 4 } as const

/**
 * Matomo sites that have "Campaign" and "Org and language" as dimensions 3 and 4. Matomo numbers
 * dimensions per site, so any other site gets the event without them rather than in a slot that
 * may mean something else there.
 */
export const MATOMO_SITES: readonly string[] = [
  '1', // djurensratt.se + stod.djurensratt.se
  '4', // project1882.org + donate.project1882.org
]

// Lowercase ids only. Six digits in a row, counted across separators, are refused too, so no
// personnummer or phone number fits, formatted or not.
const ID = /^[a-z0-9][a-z0-9:_/-]{0,79}$/
const LONG_DIGITS = /\d{6}/

function safeId(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined
  const id = value.trim().toLowerCase()
  return ID.test(id) && !LONG_DIGITS.test(id.replace(/[^a-z0-9]/g, '')) ? id : undefined
}

// A campaign code is an upper-case CRM code (`KONVSMSAG24B`, `1101001`) or one of the CRM's
// lower-case gift-structure aliases (crm `LogisktIdForGavostrukturen`). `?campaignCode` is free
// text in a URL, so anything else is dropped, and so is a code with eight or more digits.
const CRM_CODE = /^[A-Z0-9][A-Z0-9_-]{1,31}$/
const MANY_DIGITS = /(\d.*){8}/
const CRM_ALIASES = new Set([
  'web-donation',
  'webbgava_foretag',
  'footer-swish',
  'minnesgava_webb',
  'gavogram_webb',
  'julgava_webb',
  'julgavogram_webb',
  'medlemskap_webb',
  'smsgava',
  'skattegava',
])

function safeCode(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined
  const code = value.trim()
  if (CRM_ALIASES.has(code)) return code
  return CRM_CODE.test(code) && !MANY_DIGITS.test(code) ? code : undefined
}

/** The event Matomo gets, or `null` when its type or action doesn't pass the allow-list. */
export function toMatomoEvent(event: TrackEvent): MatomoEvent | null {
  const category = FORM_TYPES.find((type) => type === event.type)
  const action = safeId(event.action)
  if (!category || !action) return null
  const out: MatomoEvent = { category, action }
  const name = safeId(event.name)
  if (name) out.name = name
  if (typeof event.value === 'number' && Number.isFinite(event.value) && event.value > 0) {
    out.value = Math.round(event.value)
  }
  const campaign = safeCode(event.campaign)
  if (campaign) out.campaign = campaign
  const org = FORM_ORGS.find((o) => o === event.org)
  const lang = typeof event.lang === 'string' && /^[a-z]{2}$/.test(event.lang) ? event.lang : ''
  if (org) out.orgLang = lang ? `${org}-${lang}` : org
  return out
}

/** The part of Matomo's tracker a queued function uses. */
export interface MatomoTracker {
  getSiteId(): string | number
  trackEvent(
    category: string,
    action: string,
    name?: string,
    value?: number,
    customData?: Record<string, string>,
  ): void
}

/**
 * Queue the event on `window._paq`. A no-op on the server and on a staff test page; never throws.
 */
export function track(event: TrackEvent): void {
  try {
    if (typeof window === 'undefined') return
    if (isStaffTest(new URLSearchParams(window.location?.search))) return
    const e = toMatomoEvent(event)
    if (!e) return
    const w = window as unknown as { _paq?: unknown[] }
    // The tracker replays entries queued before it loaded, and runs a queued function with itself
    // as `this`: that's how the event finds its site's dimensions.
    w._paq = w._paq || []
    w._paq.push([
      function (this: MatomoTracker) {
        const known = MATOMO_SITES.includes(String(this.getSiteId()))
        const custom: Record<string, string> = {}
        if (known && e.campaign) custom[`dimension${MATOMO_DIMENSIONS.campaign}`] = e.campaign
        if (known && e.orgLang) custom[`dimension${MATOMO_DIMENSIONS.orgLang}`] = e.orgLang
        this.trackEvent(
          e.category,
          e.action,
          e.name,
          e.value,
          Object.keys(custom).length ? custom : undefined,
        )
      },
    ])
  } catch {
    // Analytics must never break a donation.
  }
}
